import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  SxProps,
  Typography,
} from '@mui/material';
import { SimpleRadio } from 'Components/SimpleRadio';
import { useIsDesktop } from 'Hooks';
import { useEffect, useState } from 'react';
import { getRadioLabelTypography } from 'Utils/radioLabelTypography';

interface IControlledRadioButtons {
  title: string;
  titleSx?: SxProps;
  radioColor?: string;
  isDirectionRow: boolean;
  radioGroupSx?: SxProps;
  values: string[];
  valueSx?: SxProps;
  onChange: Function;
  ariaDescribedBy?: string;
  defaultValue?: string;
  disabled?: boolean;
}

export const ControlledRadioButtons = ({
  title,
  titleSx,
  isDirectionRow = true,
  radioGroupSx,
  values,
  valueSx,
  onChange,
  ariaDescribedBy,
  defaultValue,
  disabled,
}: IControlledRadioButtons) => {
  const isDesktop = useIsDesktop();
  const initialValue = defaultValue || values[0];
  const [selectedValue, setSelectedValue] = useState(initialValue);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  const groupId = `checkbox-group-${title.trim().replace(' ', '-')}`;

  return (
    <FormControl>
      <FormLabel
        sx={{
          mb: isDesktop ? '18px' : '14px',
          color: 'inherit',
          fontWeight: 700,
          fontSize: isDesktop ? '20px' : '18px',
          lineHeight: '24px',
          textAlign: 'center',
          fontFamily: 'inherit',
          ...titleSx,
        }}
        id={groupId}
      >
        {title}
      </FormLabel>
      <RadioGroup
        aria-label={title}
        aria-describedby={ariaDescribedBy}
        value={selectedValue}
        row={isDirectionRow}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const nextValue = event.target.value;
          setSelectedValue(nextValue);
          onChange(nextValue);
        }}
        sx={{
          gap: '16px',
          ...radioGroupSx,
        }}
      >
        {values.map((value: string, index: number) => {
          const isChecked = selectedValue === value;
          const labelTypography = getRadioLabelTypography(isChecked);
          return (
            <FormControlLabel
              aria-labelledby={groupId}
              key={index}
              value={value}
              control={
                <SimpleRadio
                  disabled={disabled ?? false}
                  slotProps={{ input: { 'aria-label': value } }}
                />
              }
              label={
                <Typography variant={labelTypography.variant}>
                  {value}
                </Typography>
              }
              sx={{
                gap: '6px',
                ml: 0,
                mr: 0,
                ...valueSx,
              }}
            />
          );
        })}
      </RadioGroup>
    </FormControl>
  );
};
